import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import CreatableSelect from 'react-select/creatable';
import { SUBMIT_FEEDBACK } from '../../graphql/feedback';
import { ME_QUERY } from '../../graphql/auth';
import { uploadEvidenceFile } from '../../services/feedbackUpload';
import { trackEvent } from '../../lib/analytics';
import { useToast } from '../../hooks/useToast.js';
import { useError } from '../../hooks/useError.js';
import {
  GET_CATEGORIES,
  GET_METABOLITES,
  GET_SPECIES,
  GET_THERAPEUTIC_EFFECTS,
} from '../../services/api';

const TYPES = [
  { key: 'ERROR',       label: 'Erro'     },
  { key: 'IMPROVEMENT', label: 'Melhoria' },
  { key: 'CONTENT',     label: 'Conteúdo' },
  { key: 'QUESTION',    label: 'Dúvida'   },
];

const SUBJECTS = [
  { key: 'DESIGN',       label: 'Design' },
  { key: 'UX',           label: 'Experiência de uso' },
  { key: 'BUG',          label: 'Bug' },
  { key: 'CONTENT',      label: 'Conteúdo' },
  { key: 'ORGANIZATION', label: 'Organização' },
  { key: 'OTHER',        label: 'Outro' },
];

const SOURCE_KINDS = [
  { key: 'URL', label: 'URL' },
  { key: 'DOI', label: 'DOI' },
  { key: 'PDF', label: 'Arquivo PDF' },
];

const initialState = {
  type: 'ERROR',
  title: '',
  body: '',
  subject: '',
  sourceKind: 'URL',
  sourceUrl: '',
  sourceDoi: '',
  contentSubtype: 'BIBLIOGRAPHIC', // BIBLIOGRAPHIC | PLANT_PHOTO | SPECIE_RELATIONSHIP_SUGGESTION
  specieId: '',
  suggestedTherapeuticEffects: [],
  suggestedCategories: [],
  suggestedMetabolites: [],
  suggestionEvidenceText: '',
  suggestionSourceKind: 'URL',
  suggestionSourceUrl: '',
  suggestionSourceDoi: '',
  suggestionPdfFile: null,
  authorshipAccepted: false,
  imageFiles: [],
  pdfFile: null,
};

const toSelectOption = (item, labelKey) => ({
  value: String(item.id),
  label: item[labelKey],
  id: item.id,
  isNew: false,
});

const toSuggestionItems = (items) => items.map((item) => {
  if (item.__isNew__ || item.isNew) {
    return { label: item.label.trim(), isNew: true };
  }
  return { id: parseInt(item.value, 10), label: item.label };
});

const selectTheme = (isDarkTheme) => ({
  control: (base) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    backgroundColor: isDarkTheme ? 'rgba(3, 7, 18, 0.3)' : '#fff',
    borderColor: isDarkTheme ? '#374151' : '#d1d5db',
    color: isDarkTheme ? '#f9fafb' : '#111827',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 60,
    backgroundColor: isDarkTheme ? '#111827' : '#fff',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? (isDarkTheme ? '#1f2937' : '#f3f4f6')
      : (isDarkTheme ? '#111827' : '#fff'),
    color: isDarkTheme ? '#f9fafb' : '#111827',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: isDarkTheme ? '#064e3b' : '#dcfce7',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: isDarkTheme ? '#ecfdf5' : '#14532d',
  }),
  input: (base) => ({
    ...base,
    color: isDarkTheme ? '#f9fafb' : '#111827',
  }),
  singleValue: (base) => ({
    ...base,
    color: isDarkTheme ? '#f9fafb' : '#111827',
  }),
});

/**
 * Ícone ℹ️ com tooltip explicando que dúvidas não contam como contribuição.
 * Regra de negócio: conforme Termos de Uso (Regras de contribuição), somente
 * feedbacks dos tipos Erro, Melhoria e Conteúdo pontuam quando aprovados.
 */
const QuestionInfoTooltip = ({ isDarkTheme }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const show = () => {
    clearTimeout(timerRef.current);
    setVisible(true);
  };

  const hide = () => {
    timerRef.current = setTimeout(() => setVisible(false), 150);
  };

  return (
    <span className="relative inline-flex items-center" onMouseEnter={show} onMouseLeave={hide}>
      <span
        role="img"
        aria-label="Informação sobre dúvidas"
        className="ml-1.5 cursor-help select-none text-[13px] opacity-70 hover:opacity-100 transition-opacity"
      >
        ℹ️
      </span>

      {visible && (
        <span
          role="tooltip"
          className={clsx(
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg px-3 py-2',
            'text-xs leading-relaxed shadow-lg z-[70] pointer-events-auto',
            isDarkTheme
              ? 'bg-gray-900 text-gray-100 border border-gray-700'
              : 'bg-white text-gray-800 border border-gray-200'
          )}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          Dúvidas <strong>não pontuam</strong> como contribuição.{' '}
          Apenas <em>Erro</em>, <em>Melhoria</em> e <em>Conteúdo</em> geram pontos quando aprovados.{' '}
          <Link
            to="/termos"
            target="_blank"
            rel="noreferrer"
            className="underline font-medium text-greenbook-light-primary"
          >
            Ver Termos de Uso
          </Link>
          .
          <span
            className={clsx(
              'absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent',
              isDarkTheme ? 'border-t-gray-900' : 'border-t-white'
            )}
          />
        </span>
      )}
    </span>
  );
};

QuestionInfoTooltip.propTypes = {
  isDarkTheme: PropTypes.bool,
};

QuestionInfoTooltip.defaultProps = {
  isDarkTheme: false,
};

const FeedbackModal = ({ isOpen, onClose, isDarkTheme, profile }) => {
  const toast = useToast();
  const { showError } = useError();
  const [submitFeedback, { loading }] = useMutation(SUBMIT_FEEDBACK, {
    refetchQueries: [{ query: ME_QUERY }],
    awaitRefetchQueries: true,
  });
  const { data: speciesData } = useQuery(GET_SPECIES, {
    skip: !isOpen,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar espécies.' }),
  });
  const { data: therapeuticEffectsData } = useQuery(GET_THERAPEUTIC_EFFECTS, {
    skip: !isOpen,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar efeitos terapêuticos.' }),
  });
  const { data: categoriesData } = useQuery(GET_CATEGORIES, {
    skip: !isOpen,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar categorias.' }),
  });
  const { data: metabolitesData } = useQuery(GET_METABOLITES, {
    skip: !isOpen,
    onError: (error) => showError(error, { fallback: 'Falha ao carregar metabólitos.' }),
  });
  const [form, setForm] = useState(initialState);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const reset = () => setForm(initialState);

  const close = () => {
    reset();
    setError(null);
    onClose();
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 5);
    setField('imageFiles', files);
    trackEvent('file_upload', {
      form_name: 'feedback',
      file_type: 'image',
      file_count: files.length,
    });
  };

  const handlePdfChange = (event) => {
    const file = event.target.files?.[0] || null;
    setField('pdfFile', file);
    trackEvent('file_upload', {
      form_name: 'feedback',
      file_type: 'pdf',
      file_count: file ? 1 : 0,
    });
  };

  const handleSuggestionPdfChange = (event) => {
    const file = event.target.files?.[0] || null;
    setField('suggestionPdfFile', file);
    trackEvent('file_upload', {
      form_name: 'feedback',
      file_type: 'suggestion_pdf',
      file_count: file ? 1 : 0,
    });
  };

  const validate = () => {
    if (!form.title.trim()) return 'Informe um título.';
    if (!form.body.trim()) return 'Descreva sua contribuição.';
    if ((form.type === 'ERROR' || form.type === 'IMPROVEMENT') && !form.subject) {
      return 'Selecione um assunto.';
    }
    if (form.type === 'CONTENT' && form.contentSubtype === 'BIBLIOGRAPHIC') {
      if (form.sourceKind === 'URL' && !form.sourceUrl.trim()) return 'Informe a URL da fonte.';
      if (form.sourceKind === 'DOI' && !form.sourceDoi.trim()) return 'Informe o DOI da fonte.';
      if (form.sourceKind === 'PDF' && !form.pdfFile) return 'Anexe o PDF da fonte.';
    }
    if (form.type === 'CONTENT' && form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION') {
      if (!form.specieId || Number.isNaN(parseInt(form.specieId, 10))) {
        return 'Selecione a espécie.';
      }
      const totalSuggestions = form.suggestedTherapeuticEffects.length
        + form.suggestedCategories.length
        + form.suggestedMetabolites.length;
      if (totalSuggestions === 0) {
        return 'Sugira pelo menos um efeito, categoria ou metabólito.';
      }
      if (!form.suggestionEvidenceText.trim()) {
        if (form.suggestionSourceKind === 'URL' && !form.suggestionSourceUrl.trim()) return 'Informe a URL da evidência.';
        if (form.suggestionSourceKind === 'DOI' && !form.suggestionSourceDoi.trim()) return 'Informe o DOI da evidência.';
        if (form.suggestionSourceKind === 'PDF' && !form.suggestionPdfFile) return 'Anexe o PDF da evidência.';
      }
    }
    if (form.type === 'CONTENT' && form.contentSubtype === 'PLANT_PHOTO') {
      if (!form.specieId || Number.isNaN(parseInt(form.specieId, 10))) {
        return 'Informe o ID da espécie. (Em breve teremos um autocomplete.)';
      }
      if (form.imageFiles.length === 0) return 'Anexe pelo menos uma foto.';
      if (!form.authorshipAccepted) return 'Declare a autoria das imagens para continuar.';
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    const msg = validate();
    if (msg) {
      setError(msg);
      trackEvent('form_error', {
        form_name: 'feedback',
        step_name: 'submit',
        feedback_type: form.type,
        content_subtype: form.type === 'CONTENT' ? form.contentSubtype : null,
        error_code: 'validation_failed',
      });
      return;
    }

    try {
      const evidences = [];

      // Upload de imagens
      const allowedImageKinds = ['ERROR', 'IMPROVEMENT', 'QUESTION'].includes(form.type)
        || (form.type === 'CONTENT' && form.contentSubtype === 'PLANT_PHOTO');
      if (allowedImageKinds) {
        for (const file of form.imageFiles) {
          const up = await uploadEvidenceFile(file, 'image');
          evidences.push({ kind: 'file', storageKey: up.storageKey, mimeType: up.mimeType, sizeBytes: up.sizeBytes });
        }
      }

      // Upload de PDF
      if (form.pdfFile) {
        const up = await uploadEvidenceFile(form.pdfFile, 'pdf');
        evidences.push({ kind: 'file', storageKey: up.storageKey, mimeType: up.mimeType, sizeBytes: up.sizeBytes });
      }
      if (
        form.type === 'CONTENT'
        && form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION'
        && form.suggestionSourceKind === 'PDF'
        && form.suggestionPdfFile
      ) {
        const up = await uploadEvidenceFile(form.suggestionPdfFile, 'pdf');
        evidences.push({ kind: 'file', storageKey: up.storageKey, mimeType: up.mimeType, sizeBytes: up.sizeBytes });
      }

      // Links de fonte (CONTENT bibliográfico)
      if (form.type === 'CONTENT' && form.contentSubtype === 'BIBLIOGRAPHIC') {
        if (form.sourceKind === 'URL' && form.sourceUrl.trim()) {
          evidences.push({ kind: 'link', linkType: 'url', linkValue: form.sourceUrl.trim() });
        } else if (form.sourceKind === 'DOI' && form.sourceDoi.trim()) {
          evidences.push({ kind: 'link', linkType: 'doi', linkValue: form.sourceDoi.trim() });
        }
      }
      if (form.type === 'CONTENT' && form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION') {
        if (form.suggestionSourceKind === 'URL' && form.suggestionSourceUrl.trim()) {
          evidences.push({ kind: 'link', linkType: 'url', linkValue: form.suggestionSourceUrl.trim(), label: 'Evidência da sugestão' });
        } else if (form.suggestionSourceKind === 'DOI' && form.suggestionSourceDoi.trim()) {
          evidences.push({ kind: 'link', linkType: 'doi', linkValue: form.suggestionSourceDoi.trim(), label: 'Evidência da sugestão' });
        }
      }

      const meta = {
        contextPath: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
      };
      if (form.type === 'ERROR' || form.type === 'IMPROVEMENT') {
        meta.subject = form.subject;
      }
      if (form.type === 'CONTENT') {
        if (form.contentSubtype === 'PLANT_PHOTO') {
          meta.contentSubtype = 'PLANT_PHOTO';
          meta.authorshipText = 'Declaro que as imagens enviadas são de minha autoria ou possuo autorização para uso, e cedo licença não exclusiva ao Greenbook para uso interno e divulgação.';
          meta.authorshipDeclaredAt = new Date().toISOString();
        } else if (form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION') {
          meta.contentSubtype = 'SPECIE_RELATIONSHIP_SUGGESTION';
          meta.evidenceText = form.suggestionEvidenceText.trim() || null;
          meta.therapeuticEffects = toSuggestionItems(form.suggestedTherapeuticEffects);
          meta.categories = toSuggestionItems(form.suggestedCategories);
          meta.metabolites = toSuggestionItems(form.suggestedMetabolites);
        } else {
          meta.sourceKind = form.sourceKind;
        }
      }

      const specieId = form.type === 'CONTENT' && ['PLANT_PHOTO', 'SPECIE_RELATIONSHIP_SUGGESTION'].includes(form.contentSubtype)
        ? parseInt(form.specieId, 10)
        : null;

      const res = await submitFeedback({
        variables: {
          input: {
            type: form.type,
            title: form.title.trim(),
            body: form.body.trim(),
            specieId,
            meta,
            evidences,
          },
        },
      });

      const milestones = res.data?.submitFeedback?.milestonesReached || [];
      trackEvent('feedback_submit', {
        feedback_type: form.type,
        content_subtype: form.type === 'CONTENT' ? form.contentSubtype : null,
        has_file: evidences.some((e) => e.kind === 'file'),
        evidence_count: evidences.length,
        milestone_count: milestones.length,
        eligible_for_rewards: form.type !== 'QUESTION',
      });
      if (milestones.length > 0) {
        toast.success(`Marco atingido: ${milestones.map((m) => m.label).join(', ')} 🎉`);
      } else {
        toast.success('Contribuição enviada. Obrigado!');
      }
      close();
    } catch (err) {
      const message = showError(err, { fallback: 'Falha ao enviar feedback.' });
      setError(message);
      trackEvent('form_error', {
        form_name: 'feedback',
        step_name: 'submit',
        feedback_type: form.type,
        content_subtype: form.type === 'CONTENT' ? form.contentSubtype : null,
        error_code: 'submit_failed',
      });
    }
  };

  const showSubject = form.type === 'ERROR' || form.type === 'IMPROVEMENT';
  const showContent = form.type === 'CONTENT';
  const showBiblio = showContent && form.contentSubtype === 'BIBLIOGRAPHIC';
  const showPlantPhoto = showContent && form.contentSubtype === 'PLANT_PHOTO';
  const showRelationshipSuggestion = showContent && form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION';
  const allowImages = ['ERROR', 'IMPROVEMENT', 'QUESTION'].includes(form.type) || showPlantPhoto;
  const allowPdfInput = (showContent && form.contentSubtype === 'BIBLIOGRAPHIC' && form.sourceKind === 'PDF')
    || form.type === 'QUESTION';
  const speciesOptions = (speciesData?.species || []).map((specie) => toSelectOption(specie, 'name'));
  const therapeuticEffectOptions = (therapeuticEffectsData?.therapeuticEffects || []).map((effect) => toSelectOption(effect, 'term'));
  const categoryOptions = (categoriesData?.categories || []).map((category) => toSelectOption(category, 'name'));
  const metaboliteOptions = (metabolitesData?.metabolites || []).map((metabolite) => toSelectOption(metabolite, 'name'));
  const selectedSpecie = speciesOptions.find((option) => option.value === form.specieId) || null;
  const relationshipEvidenceByFile = showRelationshipSuggestion && form.suggestionSourceKind === 'PDF';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={handleSubmit}
        className={clsx(
          'w-full max-w-2xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]',
          isDarkTheme ? 'bg-greenbook-dark-bg text-greenbook-dark-text' : 'bg-white text-gray-900'
        )}
      >
        <div className="p-6 pb-3 border-b border-gray-200 dark:border-gray-800 space-y-3">
          <h2 className="text-xl font-semibold">Enviar feedback</h2>
          <div className="flex flex-wrap gap-2 text-sm">
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setField('type', t.key)}
                className={clsx(
                  'px-3 py-1 rounded-lg border transition-colors inline-flex items-center',
                  form.type === t.key
                    ? 'bg-greenbook-light-primary text-white border-transparent'
                    : 'border-gray-300 dark:border-gray-700 hover:border-greenbook-light-primary'
                )}
              >
                {t.label}
                {t.key === 'QUESTION' && (
                  <QuestionInfoTooltip isDarkTheme={isDarkTheme} />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título</label>
            <input
              type="text"
              className={clsx(
                'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2',
                isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
              )}
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              maxLength={140}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              rows={4}
              className={clsx(
                'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2',
                isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
              )}
              value={form.body}
              onChange={(e) => setField('body', e.target.value)}
            />
          </div>

          {showSubject && (
            <div>
              <label className="block text-sm font-medium mb-1">Assunto</label>
              <select
                className={clsx(
                  'w-full rounded-lg border px-3 py-2 text-sm',
                  isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                )}
                value={form.subject}
                onChange={(e) => setField('subject', e.target.value)}
              >
                <option value="">Selecione…</option>
                {SUBJECTS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {showContent && (
            <div className="space-y-3">
              <div className="flex gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => setField('contentSubtype', 'BIBLIOGRAPHIC')}
                  className={clsx(
                    'px-3 py-1 rounded-lg border',
                    form.contentSubtype === 'BIBLIOGRAPHIC'
                      ? 'bg-greenbook-light-primary text-white border-transparent'
                      : 'border-gray-300 dark:border-gray-700'
                  )}
                >
                  Fonte bibliográfica
                </button>
                <button
                  type="button"
                  onClick={() => setField('contentSubtype', 'PLANT_PHOTO')}
                  className={clsx(
                    'px-3 py-1 rounded-lg border',
                    form.contentSubtype === 'PLANT_PHOTO'
                      ? 'bg-greenbook-light-primary text-white border-transparent'
                      : 'border-gray-300 dark:border-gray-700'
                  )}
                >
                  Foto de planta
                </button>
                <button
                  type="button"
                  onClick={() => setField('contentSubtype', 'SPECIE_RELATIONSHIP_SUGGESTION')}
                  className={clsx(
                    'px-3 py-1 rounded-lg border',
                    form.contentSubtype === 'SPECIE_RELATIONSHIP_SUGGESTION'
                      ? 'bg-greenbook-light-primary text-white border-transparent'
                      : 'border-gray-300 dark:border-gray-700'
                  )}
                >
                  Sugerir efeito/categoria
                </button>
              </div>

              {showBiblio && (
                <>
                  <div className="flex gap-2 text-sm">
                    {SOURCE_KINDS.map((s) => (
                      <label
                        key={s.key}
                        className={clsx(
                          'px-3 py-1 rounded-lg border cursor-pointer',
                          form.sourceKind === s.key
                            ? 'bg-greenbook-light-primary text-white border-transparent'
                            : 'border-gray-300 dark:border-gray-700'
                        )}
                      >
                        <input
                          type="radio"
                          className="hidden"
                          name="sourceKind"
                          value={s.key}
                          checked={form.sourceKind === s.key}
                          onChange={(e) => setField('sourceKind', e.target.value)}
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                  {form.sourceKind === 'URL' && (
                    <input
                      type="url"
                      placeholder="https://..."
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.sourceUrl}
                      onChange={(e) => setField('sourceUrl', e.target.value)}
                    />
                  )}
                  {form.sourceKind === 'DOI' && (
                    <input
                      type="text"
                      placeholder="10.xxxx/..."
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.sourceDoi}
                      onChange={(e) => setField('sourceDoi', e.target.value)}
                    />
                  )}
                </>
              )}

              {showPlantPhoto && (
                <div className="space-y-2">
                  <div className="text-xs bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-2 text-yellow-900 dark:text-yellow-100">
                    Você ganha <strong>1 ponto por espécie</strong> — fotos adicionais da mesma espécie são aceitas, mas não pontuam.
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ID da espécie</label>
                    <input
                      type="number"
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.specieId}
                      onChange={(e) => setField('specieId', e.target.value)}
                    />
                  </div>
                  <label className="flex gap-2 items-start text-xs">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={form.authorshipAccepted}
                      onChange={(e) => setField('authorshipAccepted', e.target.checked)}
                    />
                    <span>
                      Declaro que as imagens enviadas são de minha autoria ou possuo autorização para uso, e cedo licença
                      não exclusiva ao Greenbook para uso interno e divulgação.
                    </span>
                  </label>
                </div>
              )}

              {showRelationshipSuggestion && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Espécie</label>
                    <CreatableSelect
                      isClearable
                      isValidNewOption={() => false}
                      options={speciesOptions}
                      value={selectedSpecie}
                      onChange={(option) => setField('specieId', option?.value || '')}
                      placeholder="Selecione a espécie..."
                      styles={selectTheme(isDarkTheme)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Efeitos terapêuticos sugeridos</label>
                    <CreatableSelect
                      isMulti
                      options={therapeuticEffectOptions}
                      value={form.suggestedTherapeuticEffects}
                      onChange={(value) => setField('suggestedTherapeuticEffects', value || [])}
                      placeholder="Selecione ou digite um novo efeito..."
                      formatCreateLabel={(value) => `Sugerir novo efeito: ${value}`}
                      styles={selectTheme(isDarkTheme)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Categorias sugeridas</label>
                    <CreatableSelect
                      isMulti
                      options={categoryOptions}
                      value={form.suggestedCategories}
                      onChange={(value) => setField('suggestedCategories', value || [])}
                      placeholder="Selecione ou digite uma nova categoria..."
                      formatCreateLabel={(value) => `Sugerir nova categoria: ${value}`}
                      styles={selectTheme(isDarkTheme)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Metabólitos sugeridos</label>
                    <CreatableSelect
                      isMulti
                      options={metaboliteOptions}
                      value={form.suggestedMetabolites}
                      onChange={(value) => setField('suggestedMetabolites', value || [])}
                      placeholder="Selecione ou digite um novo metabólito..."
                      formatCreateLabel={(value) => `Sugerir novo metabólito: ${value}`}
                      styles={selectTheme(isDarkTheme)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Evidência em texto</label>
                    <textarea
                      rows={3}
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.suggestionEvidenceText}
                      onChange={(e) => setField('suggestionEvidenceText', e.target.value)}
                    />
                  </div>

                  <div className="flex gap-2 text-sm flex-wrap">
                    {SOURCE_KINDS.map((s) => (
                      <label
                        key={s.key}
                        className={clsx(
                          'px-3 py-1 rounded-lg border cursor-pointer',
                          form.suggestionSourceKind === s.key
                            ? 'bg-greenbook-light-primary text-white border-transparent'
                            : 'border-gray-300 dark:border-gray-700'
                        )}
                      >
                        <input
                          type="radio"
                          className="hidden"
                          name="suggestionSourceKind"
                          value={s.key}
                          checked={form.suggestionSourceKind === s.key}
                          onChange={(e) => setField('suggestionSourceKind', e.target.value)}
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>

                  {form.suggestionSourceKind === 'URL' && (
                    <input
                      type="url"
                      placeholder="https://..."
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.suggestionSourceUrl}
                      onChange={(e) => setField('suggestionSourceUrl', e.target.value)}
                    />
                  )}

                  {form.suggestionSourceKind === 'DOI' && (
                    <input
                      type="text"
                      placeholder="10.xxxx/..."
                      className={clsx(
                        'w-full rounded-lg border px-3 py-2 text-sm',
                        isDarkTheme ? 'bg-gray-950/30 border-gray-700' : 'bg-white border-gray-300'
                      )}
                      value={form.suggestionSourceDoi}
                      onChange={(e) => setField('suggestionSourceDoi', e.target.value)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {allowImages && (
            <div>
              <label className="block text-sm font-medium mb-1">
                {showPlantPhoto ? 'Fotos da planta' : 'Imagens (opcional)'}
              </label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                onChange={handleImagesChange}
                className="text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Até 5 arquivos, máx 5MB cada (PNG/JPEG/WebP).</p>
            </div>
          )}

          {allowPdfInput && (
            <div>
              <label className="block text-sm font-medium mb-1">PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfChange}
                className="text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Máx 25MB.</p>
            </div>
          )}

          {relationshipEvidenceByFile && (
            <div>
              <label className="block text-sm font-medium mb-1">PDF da evidência</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleSuggestionPdfChange}
                className="text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Máx 25MB.</p>
            </div>
          )}

          {error && <p className="text-sm text-red-600 dark:text-red-300">{error}</p>}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-800 text-xs flex items-center justify-between gap-2">
          <span className="text-gray-600 dark:text-gray-300">
            Acompanhamento por WhatsApp: {profile?.whatsapp ? `+${profile.whatsapp.replace(/^\+/, '')}` : '—'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={close} className="px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" disabled={loading}>
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg font-semibold bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900 disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Enviando…' : 'Enviar feedback'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

FeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isDarkTheme: PropTypes.bool,
  profile: PropTypes.shape({
    whatsapp: PropTypes.string,
  }),
};

FeedbackModal.defaultProps = {
  isDarkTheme: false,
  profile: null,
};

export default FeedbackModal;
